import styled from 'styled-components';

import { style } from '@genially/design-system';

import {
  withFadeInAnimation,
  withFadeInScaleInAnimation,
  withSlideInRightFadeIn,
  withSlideInUpFadeIn,
} from './animations.styled';

export const LogoWrapper = styled.div(({ theme: { size } }) => ({
  height: size.units(6),
  cursor: 'pointer',

  ...style.mixins.responsive.xs.downward({
    height: size.units(5),
  }),

  svg: {
    width: 'auto',
  },
}));

export const BannerWrapper = styled.div({
  width: '100%',
});

export const Wrapper = styled.div(({ theme: { size } }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  padding: `${size.units(7)} ${size.units(9)} ${size.units(10)}`,
  gap: size.units(3.5),
  height: '100dvh',
  overflow: 'hidden',
  width: '100%',

  ...style.mixins.responsive.xs.downward({
    padding: `${size.units(4)} ${size.units(2)} ${size.units(3)} ${size.units(2)}`,
  }),

  [LogoWrapper]: {
    position: 'absolute',
    bottom: size.units(3),
    left: size.units(3),
  },

  [BannerWrapper]: {
    position: 'absolute',
    top: 0,
    left: 0,
  },
}));

export const PlayersContent = styled.div({
  overflow: 'hidden',
  width: '100%',
  height: '100%',

  ...style.mixins.responsive.xs.downward({
    overflow: 'visible',
  }),
});

interface SharedContentWrapperProps {
  $show?: boolean;
}

const SharedContentWrapper = styled.div<SharedContentWrapperProps>(
  ({ theme: { borderRadius, color, size }, $show = false }) => ({
    padding: `${size.units(2)} ${size.units(3)}`,
    backgroundColor: 'rgba(199, 195, 211, 0.05)',
    borderRadius: borderRadius.medium,
    opacity: $show ? 1 : 0,
    transition: 'opacity 0.3s ease-in-out',

    ...style.mixins.responsive.xs.downward({
      padding: 0,
      backgroundColor: color.background.reversed.default(),
    }),
  }),
);

export const HeaderContentWrapper = withSlideInUpFadeIn(
  styled(SharedContentWrapper)(({ theme: { color, size } }) => ({
    ...style.mixins.responsive.xs.downward({
      borderBottom: `1px solid ${color.border.primary.default()}`,
      borderRadius: 0,
      paddingBottom: size.units(2),
    }),
  })),
);

interface InfoContentWrapperProps {
  $centered?: boolean;
}

export const InfoContentWrapper = withFadeInScaleInAnimation(
  styled(SharedContentWrapper)<InfoContentWrapperProps>(
    ({ theme: { size }, $centered }) => {
      return $centered
        ? {
            opacity: 0,
            padding: size.units(8),
          }
        : {
            ...style.mixins.responsive.xs.downward({
              zIndex: 1,
            }),
          };
    },
  ),
);

export const ParticipantsContentWrapper = withSlideInRightFadeIn(
  styled(SharedContentWrapper)(({ theme: { size } }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: size.units(2),
    minHeight: 0,
  })),
);

interface StartButtonWrapperProps {
  $show?: boolean;
}

export const StartButtonWrapper = withFadeInAnimation(
  styled.div<StartButtonWrapperProps>(({ $show }) => ({
    opacity: $show ? 1 : 0,
  })),
);

interface ContentWrapperProps {
  $isInfoCentered?: boolean;
  $participantsStickyTop?: number;
}

export const ContentWrapper = styled.div<ContentWrapperProps>(
  ({ theme: { size }, $isInfoCentered, $participantsStickyTop }) => ({
    display: 'grid',
    gridTemplateRows: `${size.units(15.5)} 1fr`,
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridRowGap: size.units(3),
    gridColumnGap: size.units(2),
    height: '100%',

    [HeaderContentWrapper]: {
      gridArea: '1 / 1 / 2 / 3',
    },

    [InfoContentWrapper]: {
      gridArea: '2 / 1 / 3 / 2',

      ...($isInfoCentered
        ? {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) scale(1)',
          }
        : {}),
    },

    [ParticipantsContentWrapper]: {
      gridArea: '2 / 2 / 3 / 3',
    },

    ...style.mixins.responsive.xs.downward({
      display: 'flex',
      flexDirection: 'column',
      gap: size.units(2),
      overflowY: 'auto',

      [HeaderContentWrapper]: {
        order: 1,
        marginTop: size.units(2),
      },

      [InfoContentWrapper]: {
        order: 0,

        position: $isInfoCentered ? 'sticky' : undefined,
        top: $isInfoCentered ? 0 : undefined,
      },

      [ParticipantsContentWrapper]: {
        order: 2,
        flex: 1,
        position: 'sticky',
        top: $participantsStickyTop,
      },
    }),
  }),
);

export const ParticipantsInfoWrapper = styled.div(({ theme: { color } }) => ({
  ...style.mixins.responsive.xs.downward({
    backgroundColor: color.background.reversed.default(),
    zIndex: 2,
  }),
}));
